import React from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useSubscriptionStatus } from '../hooks/useSubscriptionStatus';
import { useAuth } from '../context/AuthContext';
import { UserRole } from '../types';

/** Routes a locked-out staff user can still reach (settings + account management). */
const EXEMPT_PREFIXES = ['/admin/settings', '/account', '/change-password'];

const PAYSTACK_URL = import.meta.env.VITE_PAYSTACK_UPGRADE_URL as string | undefined;
const SUPPORT_EMAIL =
  (import.meta.env.VITE_SUPPORT_EMAIL as string | undefined) ?? 'hello@kulalearn.com';

const StaffLockScreen: React.FC = () => (
  <div
    style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'var(--staff-bg)',
      color: 'var(--staff-ink)',
      padding: '2rem',
      textAlign: 'center',
    }}
  >
    <div style={{ maxWidth: '28rem' }}>
      <div style={{ fontSize: '2.5rem', marginBottom: '1rem', lineHeight: 1 }}>⏸</div>
      <h1
        style={{
          fontSize: '1.5rem',
          fontWeight: 700,
          marginBottom: '0.75rem',
          fontFamily: 'inherit',
        }}
      >
        Your trial has expired
      </h1>
      <p
        style={{
          color: 'var(--staff-ink-2)',
          marginBottom: '1.75rem',
          lineHeight: 1.6,
          fontSize: '0.9375rem',
        }}
      >
        Your free trial and grace period have ended. Upgrade to restore full access, or contact us
        if you need assistance.
      </p>
      <div
        style={{
          display: 'flex',
          gap: '0.75rem',
          justifyContent: 'center',
          flexWrap: 'wrap',
        }}
      >
        {PAYSTACK_URL && (
          <a
            href={PAYSTACK_URL}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              background: 'var(--staff-accent)',
              color: '#fff',
              padding: '0.625rem 1.25rem',
              borderRadius: '0.5rem',
              fontWeight: 600,
              fontSize: '0.9375rem',
              textDecoration: 'none',
            }}
          >
            Upgrade now
          </a>
        )}
        <a
          href={`mailto:${SUPPORT_EMAIL}`}
          style={{
            background: 'var(--staff-surface)',
            border: '1px solid var(--staff-line)',
            color: 'var(--staff-ink)',
            padding: '0.625rem 1.25rem',
            borderRadius: '0.5rem',
            fontWeight: 500,
            fontSize: '0.9375rem',
            textDecoration: 'none',
          }}
        >
          Contact us
        </a>
      </div>
      <p style={{ marginTop: '1.75rem', fontSize: '0.875rem', color: 'var(--staff-ink-3)' }}>
        You can still access{' '}
        <a href="/admin/settings" style={{ color: 'var(--staff-accent)', textDecoration: 'none' }}>
          account settings
        </a>
        .
      </p>
    </div>
  </div>
);

const StudentPausedScreen: React.FC = () => (
  <div
    style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#f9fafb',
      color: '#111827',
      padding: '2rem',
      textAlign: 'center',
    }}
  >
    <div style={{ maxWidth: '28rem' }}>
      <div style={{ fontSize: '2.5rem', marginBottom: '1rem', lineHeight: 1 }}>⏸</div>
      <h1 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '0.75rem' }}>
        This school is currently paused
      </h1>
      <p style={{ color: '#6b7280', lineHeight: 1.6, fontSize: '0.9375rem' }}>
        Your learning platform is temporarily unavailable. Please contact your school administrator
        for more information.
      </p>
    </div>
  </div>
);

/**
 * Pathless layout route — wraps all authenticated routes.
 * Renders Outlet normally; on 'locked' shows the appropriate full-page screen,
 * except for exempt paths (settings, account) which always pass through.
 * Fails open: while loading or on fetch error, renders Outlet.
 */
export const TrialGate: React.FC = () => {
  const location = useLocation();
  const { user } = useAuth();
  const { state, loading } = useSubscriptionStatus();

  if (loading || state !== 'locked' || user?.isSuperAdmin) {
    return <Outlet />;
  }

  const isExempt = EXEMPT_PREFIXES.some((prefix) => location.pathname.startsWith(prefix));
  if (isExempt) return <Outlet />;

  const isStaff = user?.role === UserRole.INSTRUCTOR || user?.role === UserRole.ADMIN;
  if (isStaff) return <StaffLockScreen />;
  return <StudentPausedScreen />;
};
